import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useModalStore } from "../../store/useModalStore";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDeviceDetail } from "../../lib/apis/hooks/device.hook";
import type { DeviceKind, UpdateDeviceForm } from "../../lib/apis/dtos/request/device.request";
import { deviceErrorMessage, invalidateDeviceQueries } from "../../lib/apis/device-cache";
import { deleteDevice, deleteDeviceColor, deleteDeviceStorage, patchDevice, patchDeviceColorActive, patchDeviceStorageActive, postDeviceColor, uploadDeviceColorImage } from "../../lib/apis/v1/device.service";
import { getQueryClient } from "../../config/queryClient.config";
import FailSnackbar from "../../components/ui/snackbar/fail.component";
import SuccessSnackbar from "../../components/ui/snackbar/success.component";
import ExplainTitle from "../../components/layout/header/explain-title";
import { Checkbox, FormControlLabel, MenuItem, Select, Switch } from "@mui/material";
import { MODAL_KEY } from "../../lib/constants/modal.constants";
import { formatDate } from "../../utils/date.utils";
import { resolveImageUrl } from "../../utils/s3.utils";
import { normalizeHexColor } from "../../utils/color.utils";
import Button from "../../components/ui/button";
import FileUploadSVG from "../../assets/svg/file-upload.svg?react";

const CATEGORY_MENU = {
  IPHONE: "아이폰",
  GALAXY: "갤럭시",
  ETC: "기타",
};

const DEVICE_MODEL = Object.entries(CATEGORY_MENU).map(([value, label]) => ({
  label,
  value: value as DeviceKind,
}));

const CATEGORY_TYPE = [
  { label: "주요폰", value: "mainCheck" },
  { label: "중저가폰", value: "lowCheck" },
  { label: "키즈폰", value: "kidsCheck" },
] as const;

const EVENT_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
const toDateTimeInputValue = (value?: string | null) => value ? value.slice(0, 16).replace("T", " ") : "";
const toApiDateTime = (value: string) => `${value.replace(" ", "T")}:00`;
const toEventTime = (value: string) => new Date(value.replace(" ", "T")).getTime();
const isValidEventDateTime = (value: string) => {
  const match = value.match(EVENT_DATE_TIME_PATTERN);
  if (!match) return false;

  const [datePart, timePart] = value.split(" ");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);
  const date = new Date(year, month - 1, day, hour, minute);

  return date.getFullYear() === year
    && date.getMonth() === month - 1
    && date.getDate() === day
    && date.getHours() === hour
    && date.getMinutes() === minute;
};
const formatDateTimeInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 12);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  if (digits.length <= 8) return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
  if (digits.length <= 10) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)} ${digits.slice(8)}`;
  }
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)} ${digits.slice(8, 10)}:${digits.slice(10)}`;
};

function PencilEditButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      className="ml-2 shrink-0 disabled:cursor-not-allowed disabled:opacity-40"
      onClick={onClick}
    >
      <img
        src="/static/icons/icon_pencil_blue.svg"
        alt=""
        aria-hidden="true"
        className="h-6 w-6"
      />
    </button>
  );
}

function OptionIconButton({
  label,
  disabled,
  danger = false,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      className={`flex size-9 shrink-0 items-center justify-center rounded-[6px] transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        danger
          ? "hover:bg-[#FF3B30]/5"
          : "hover:bg-black/[0.03]"
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export default function ModelDetailPage() {
  const navigate = useNavigate();
  const { openModal, closeModalsByKey } = useModalStore();
  const { id } = useParams();

  const parsedId =
    typeof id === "string" ? id : Array.isArray(id) ? id[0] : "0";

  const { data, isSuccess, isPending, isError, error, refetch } = useQuery({
    ...useDeviceDetail(parsedId),
  });
  const { result } = data || {};
  const [eventFrom, setEventFrom] = React.useState("");
  const [eventTo, setEventTo] = React.useState("");
  const [userDisplayedTime, setUserDisplayedTime] = React.useState("");

  React.useEffect(() => {
    setEventFrom(toDateTimeInputValue(result?.eventFrom));
    setEventTo(toDateTimeInputValue(result?.eventTo));
    setUserDisplayedTime(toDateTimeInputValue(result?.userDisplayedTime));
  }, [result?.eventFrom, result?.eventTo, result?.userDisplayedTime]);

  const patchMutation = useMutation({
    mutationFn: ({ id, form }: { id: string; form: UpdateDeviceForm }) =>
      patchDevice(id, form),
    onSuccess: () => invalidateDeviceQueries(getQueryClient()),
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  });

  const deleteDeviceMutation = useMutation({
    mutationFn: () => deleteDevice(parsedId),
    onSuccess: async () => {
      await invalidateDeviceQueries(getQueryClient());
      closeModalsByKey(MODAL_KEY.Confirm);
      SuccessSnackbar("기종 삭제 완료");
      navigate("/model");
    },
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  });

  const postColorMutation = useMutation({
    mutationFn: ({ id, colorName, hexCode }: { id: string | number; colorName: string; hexCode?: string }) =>
      postDeviceColor(id, colorName, hexCode),
    onSuccess: async () => {
      await invalidateDeviceQueries(getQueryClient());
      SuccessSnackbar("색상 옵션 저장 완료");
    },
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  })

  const uploadColorImageMutation = useMutation({
    mutationFn: ({ id, colorName, file }: { id: string | number; colorName: string; file: File }) =>
      uploadDeviceColorImage(id, colorName, file),
    onSuccess: async () => {
      await invalidateDeviceQueries(getQueryClient());
      SuccessSnackbar("색상 이미지 업로드 완료");
    },
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  });

  const deleteColorMutation = useMutation({
    mutationFn: ({ id, colorName }: { id: string | number; colorName: string }) =>
      deleteDeviceColor(id, colorName),
    onSuccess: async () => {
      await invalidateDeviceQueries(getQueryClient());
      closeModalsByKey(MODAL_KEY.Confirm);
      SuccessSnackbar("색상 옵션 삭제 완료");
    },
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  })

  const colorActiveMutation = useMutation({
    mutationFn: ({ colorName, active }: { colorName: string; active: boolean }) =>
      patchDeviceColorActive(parsedId, colorName, active),
    onSuccess: async () => {
      await invalidateDeviceQueries(getQueryClient());
      SuccessSnackbar("색상 노출 상태 변경 완료");
    },
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  });

  const deleteStorageMutation = useMutation({
    mutationFn: (storage: string) => deleteDeviceStorage(parsedId, storage),
    onSuccess: async () => {
      await invalidateDeviceQueries(getQueryClient());
      closeModalsByKey(MODAL_KEY.Confirm);
      SuccessSnackbar("용량 삭제 완료");
    },
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  });

  const storageActiveMutation = useMutation({
    mutationFn: ({ storage, active }: { storage: string; active: boolean }) =>
      patchDeviceStorageActive(parsedId, storage, active),
    onSuccess: async () => {
      await invalidateDeviceQueries(getQueryClient());
      SuccessSnackbar("출고가 옵션 노출 상태 변경 완료");
    },
    onError: (error) => FailSnackbar(deviceErrorMessage(error)),
  });

  const busy = patchMutation.isPending || deleteDeviceMutation.isPending || postColorMutation.isPending
    || uploadColorImageMutation.isPending || deleteColorMutation.isPending
    || deleteStorageMutation.isPending || colorActiveMutation.isPending
    || storageActiveMutation.isPending;
  const savedUserDisplayedTime = toDateTimeInputValue(result?.userDisplayedTime);
  const userDisplayedTimeValid = isValidEventDateTime(userDisplayedTime);
  const userDisplayedTimeFuture = userDisplayedTimeValid && toEventTime(userDisplayedTime) > Date.now();
  const userDisplayedTimeChanged = userDisplayedTime !== savedUserDisplayedTime;

  if (isPending) return <p role="status" className="p-6">기종 정보를 불러오는 중입니다.</p>;
  if (isError || !result) return <div role="alert" className="p-6">
    {deviceErrorMessage(error)} <button onClick={() => void refetch()}>다시 시도</button>
  </div>;

  return (
    <div className="p-6">
      <ExplainTitle
        title="기종 관리"
        explanation="기종 정보를 관리하세요."
        backbutton={true}
        backOnclick={() => {
          getQueryClient().invalidateQueries({ queryKey: ["admin-device-list"] });
          navigate("/model");
        }}
      >
        <Button
          variant="outline-red"
          label="기종 삭제"
          disabled={busy}
          onClick={() => openModal(MODAL_KEY.Confirm, {
            title: "기종 삭제",
            content: `${result.deviceName} 기종을 삭제하시겠습니까? 삭제 후 목록에서 더 이상 노출되지 않습니다.`,
            rightButton: {
              title: "삭제",
              variant: "outline-red",
              onClick: () => deleteDeviceMutation.mutate(),
            },
          })}
        />
      </ExplainTitle>
      <table className="w-full">
        <tbody>
          <tr className="content-table-tr">
            <td className="content-table-th">기종명</td>
            <td className="content-table-td flex-row">
              {result?.deviceName || "-"}
              <PencilEditButton disabled={busy} label="기종명 수정"
                onClick={() => openModal(MODAL_KEY.TextField, {
                  title: "기종명", value: result.deviceName,
                  click: (change: string) => patchMutation.mutate({
                    id: parsedId, form: { deviceName: change.trim() },
                  }),
                })} />
            </td>
            <td className="content-table-th">모델 고유 번호</td>
            <td className="content-table-td">
              {result?.deviceCode || "-"}
            </td>
            <td className="content-table-th">생성일</td>
            <td className="content-table-td">
              {formatDate(result?.createdAt || "", 'YYYY-MM-DD HH:mm:ss') || "-"}
            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">모델 구분</td>
            <td className="content-table-td" colSpan={2}>
              <Select
                size="small"
                className="min-w-[180px] bg-white"
                value={result.category}
                disabled={busy}
                inputProps={{ "aria-label": "모델 구분" }}
                onChange={(event) => patchMutation.mutate({
                  id: parsedId,
                  form: { category: event.target.value as DeviceKind },
                })}
              >
                {DEVICE_MODEL.map(({ label: modelLabel, value: modelValue }) => (
                  <MenuItem key={modelValue} value={modelValue}>
                    {modelLabel}
                  </MenuItem>
                ))}
              </Select>
            </td>
            <td className="content-table-th" rowSpan={2}>노출 순서</td>
            <td className="content-table-td" colSpan={2} rowSpan={2}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-1">
                  {(result.displayedOrders ?? []).length > 0 ? (
                    result.displayedOrders.map(({ group, groupName, front, back }) => (
                      <p key={group} className="text-[14px]">
                        <span className="font-semibold">{groupName}</span>
                        {` : ${front?.deviceName ?? "(처음)"} - ${result.deviceName} - ${back?.deviceName ?? "(마지막)"}`}
                      </p>
                    ))
                  ) : (
                    <p className="text-[14px] text-black/50">노출 카테고리가 없습니다.</p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  label="순서 변경"
                  disabled={busy}
                  onClick={() => openModal(MODAL_KEY.DeviceOrder, { deviceId: parsedId })}
                />
              </div>
            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">카테고리</td>
            <td className="content-table-td" colSpan={2}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {CATEGORY_TYPE.map(({ label, value }) => {
                  const checked = !!result[value];

                  return (
                    <FormControlLabel
                      key={value}
                      control={(
                        <Checkbox
                          disableFocusRipple
                          checked={checked}
                          disabled={busy}
                          onChange={() => patchMutation.mutate({
                            id: parsedId,
                            form: { [value]: !checked },
                          })}
                        />
                      )}
                      label={label}
                    />
                  );
                })}
              </div>
            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th">시세표 노출</td>
            <td className="content-table-td">
              <Switch
                checked={!!result.quotationVisible}
                disabled={busy}
                onChange={() => patchMutation.mutate({
                  id: parsedId,
                  form: { quotationVisible: !result.quotationVisible },
                })}
              />
            </td>
            <td className="content-table-th">기본 시세표 포함</td>
            <td className="content-table-td">
              <Switch
                checked={!!result.isDefault}
                disabled={busy}
                onChange={() => patchMutation.mutate({
                  id: parsedId,
                  form: { isDefault: !result.isDefault },
                })}
              />
            </td>
            <td className="content-table-th">유저 노출</td>
            <td className="content-table-td">
              <div className="flex items-start gap-2">
                <Switch
                  checked={!!result.userVisible}
                  disabled={busy}
                  onChange={() => patchMutation.mutate({
                    id: parsedId,
                    form: { userVisible: !result.userVisible },
                  })}
                />
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      aria-label="유저 노출 시간"
                      value={userDisplayedTime}
                      placeholder="YYYY-MM-DD HH:mm"
                      inputMode="numeric"
                      maxLength={16}
                      disabled={busy || !!result.userVisible}
                      className="h-8 w-[156px] rounded-[6px] bg-black/[0.04] px-2 text-[13px] text-black outline-none focus:ring-2 focus:ring-[#1976D2]/30 disabled:cursor-not-allowed disabled:text-black/40"
                      onChange={(event) => setUserDisplayedTime(formatDateTimeInput(event.target.value))}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      label="저장"
                      disabled={busy || !!result.userVisible || !userDisplayedTimeFuture || !userDisplayedTimeChanged}
                      onClick={() => {
                        if (!isValidEventDateTime(userDisplayedTime)) {
                          FailSnackbar("일시는 YYYY-MM-DD HH:mm 형식으로 입력해 주세요.");
                          return;
                        }
                        if (toEventTime(userDisplayedTime) <= Date.now()) {
                          FailSnackbar("유저 노출 시간은 현재 시각 이후로 입력해 주세요.");
                          return;
                        }
                        patchMutation.mutate({
                          id: parsedId,
                          form: { userDisplayedTime: toApiDateTime(userDisplayedTime) },
                        }, {
                          onSuccess: () => SuccessSnackbar("유저 노출 시간 저장 완료"),
                        });
                      }}
                    />
                  </div>
                  {!result.userVisible && userDisplayedTime && !userDisplayedTimeFuture && (
                    <p className="text-[11px] text-[#FF3B30]">
                      {userDisplayedTimeValid
                        ? "현재 시각 이후로 입력해 주세요."
                        : "YYYY-MM-DD HH:mm 형식으로 입력해 주세요."}
                    </p>
                  )}
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <section className="mt-[47px]">
        <h2 className="mb-4 text-[22px]">이벤트</h2>
        <table className="w-full">
        <tbody>
          <tr className="content-table-tr">
            <td className="content-table-th !w-[96px] !px-4 !align-middle">이름</td>
            <td className="content-table-td !align-middle">
              <div className="flex items-center gap-2">
                <span>{result?.eventTag || "-"}</span>
                <PencilEditButton disabled={busy} label="이벤트 명 수정"
                  onClick={() => openModal(MODAL_KEY.TextField, {
                    title: "이벤트 명", value: result.eventTag ?? "",
                    click: (change: string) => patchMutation.mutate({
                      id: parsedId, form: { eventTag: change },
                    }),
                  })} />
              </div>
            </td>
            <td className="content-table-th !w-[96px] !px-4">기간</td>
            <td className="content-table-td">
              <div className="flex items-center gap-2">
                <span>{eventFrom && eventTo ? `${eventFrom} ~ ${eventTo}` : "-"}</span>
                <PencilEditButton
                  disabled={busy}
                  label="이벤트 기간 수정"
                  onClick={() => openModal(MODAL_KEY.DeviceEventPeriod, {
                    eventFrom,
                    eventTo,
                    onSave: (nextEventFrom: string, nextEventTo: string) => {
                      setEventFrom(nextEventFrom);
                      setEventTo(nextEventTo);
                      patchMutation.mutate({
                        id: parsedId,
                        form: {
                          eventFrom: toApiDateTime(nextEventFrom),
                          eventTo: toApiDateTime(nextEventTo),
                        },
                      });
                    },
                  })}
                />
              </div>
            </td>
            <td className="content-table-th !w-[96px] !px-4">활성화</td>
            <td className="content-table-td">
              <Switch
                checked={!!result.isOnEvent}
                disabled={busy}
                onChange={() => {
                  if (!result.isOnEvent && (!eventFrom || !eventTo)) {
                    FailSnackbar("이벤트 활성화 전에 시작 일시와 종료 일시를 입력해 주세요.");
                    return;
                  }
                  if (!result.isOnEvent && (!isValidEventDateTime(eventFrom) || !isValidEventDateTime(eventTo))) {
                    FailSnackbar("일시는 YYYY-MM-DD HH:mm 형식으로 입력해 주세요.");
                    return;
                  }
                  if (!result.isOnEvent && toEventTime(eventFrom) > toEventTime(eventTo)) {
                    FailSnackbar("이벤트 시작 일시는 종료 일시보다 늦을 수 없습니다.");
                    return;
                  }
                  patchMutation.mutate({
                    id: parsedId,
                    form: {
                      isOnEvent: !result?.isOnEvent,
                      ...(!result.isOnEvent ? {
                        eventFrom: toApiDateTime(eventFrom),
                        eventTo: toApiDateTime(eventTo),
                      } : {}),
                    },
                  });
                }}
              />
            </td>
          </tr>
          <tr className="content-table-tr">
            <td className="content-table-th !w-[96px] !px-4">내용</td>
            <td className="content-table-td flex-row" colSpan={5}>
              {result?.eventMessage || "-"}
              <PencilEditButton disabled={busy} label="이벤트 내용 수정"
                onClick={() => openModal(MODAL_KEY.TextField, {
                  title: "이벤트 내용", value: result.eventMessage ?? "", useTextArea: true,
                  click: (change: string) => patchMutation.mutate({
                    id: parsedId, form: { eventMessage: change },
                  }),
                })} />
            </td>
          </tr>
        </tbody>
        </table>
      </section>

      <section className="mt-[47px]">
        <h2 className="mb-4 text-[22px]">옵션</h2>
        <div className="grid grid-cols-2 gap-6">
          <div className="rounded-[14px] bg-[#F6F7F9] p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-[18px] font-semibold">색상 {result?.colorAndImage?.length || 0}</h3>
              <Button
                variant="outline"
                size="sm"
                label="+ 색상 추가"
                disabled={busy}
                onClick={() => openModal(MODAL_KEY.DeviceColor, { deviceId: parsedId })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {isSuccess && (result?.colorAndImage || []).map(({ color, imageUrl, hexCode, active }, index) => (
                <div
                  key={`${color}-${index}`}
                  className="flex min-h-[268px] flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_3px_14px_rgba(0,0,0,0.09)] transition-shadow hover:shadow-[0_5px_20px_rgba(0,0,0,0.13)]"
                >
                  <div className="flex min-w-0 items-center gap-2 bg-black/[0.035] px-4 py-3">
                    <Switch
                      size="small"
                      checked={active !== false}
                      disabled={busy}
                      inputProps={{ "aria-label": `${color} 색상 옵션 활성화` }}
                      onChange={() => colorActiveMutation.mutate({
                        colorName: color,
                        active: active === false,
                      })}
                    />
                    <p className="min-w-0 flex-1 truncate font-semibold">{color}</p>
                    <OptionIconButton
                      disabled={busy}
                      danger
                      label={`${color} 삭제`}
                      onClick={() => openModal(MODAL_KEY.Confirm, {
                        title: "색상 옵션 삭제",
                        content: `${color} 색상 옵션을 삭제하시겠습니까?`,
                        rightButton: {
                          title: "삭제",
                          variant: "outline-red",
                          onClick: () => deleteColorMutation.mutate({ id: parsedId, colorName: color }),
                        },
                      })}
                    >
                      <img src="/static/icons/icon_delete.svg" alt="" aria-hidden="true" className="size-4" />
                    </OptionIconButton>
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <div className="flex min-w-0 items-center gap-2 rounded-[10px] bg-black/[0.025] px-3 py-2">
                      <span className="w-[52px] shrink-0 text-[12px] font-medium text-black/45">헥사코드</span>
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        {hexCode && <span
                          aria-label={`${color} 색상 미리보기`}
                          className="inline-block size-5 shrink-0 rounded-full border border-black/20"
                          style={{ backgroundColor: hexCode }}
                        />}
                        <span className="min-w-0 truncate text-[14px] text-black/60">
                          {hexCode || "없음"}
                        </span>
                      </div>
                      <OptionIconButton disabled={busy} label={`${color} HEX 수정`} onClick={() =>
                        openModal(MODAL_KEY.TextField, {
                          title: "HEX 코드",
                          value: hexCode ?? "",
                          pattern: "^$|^#?(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$",
                          helper: "RGB 또는 RRGGBB 형식으로 입력해 주세요. (# 선택)",
                          click: (change: string) => postColorMutation.mutate({
                            id: parsedId,
                            colorName: color,
                            hexCode: normalizeHexColor(change),
                          }),
                        })
                      }>
                        <img src="/static/icons/icon_pencil_blue.svg" alt="" aria-hidden="true" className="size-5" />
                      </OptionIconButton>
                    </div>
                    <div className="mt-3 flex min-w-0 flex-1 items-center gap-2 rounded-[10px] bg-black/[0.025] p-3">
                      <span className="w-[52px] shrink-0 text-[12px] font-medium text-black/45">이미지</span>
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        {imageUrl && (
                          <img
                            src={resolveImageUrl(imageUrl)}
                            alt={`${color} 기기`}
                            className="size-24 shrink-0 rounded-[6px] border border-black/5 bg-black/[0.02] object-contain"
                          />
                        )}
                        {!imageUrl && <span className="min-w-0 truncate text-[14px] text-black/60">이미지 없음</span>}
                      </div>
                      <OptionIconButton disabled={busy} label={`${color} 이미지 ${imageUrl ? "변경" : "추가"}`} onClick={() =>
                        openModal(MODAL_KEY.FileUpload, {
                          title: `${color} 이미지 ${imageUrl ? "변경" : "추가"}`,
                          fileDescription: "기기 이미지를 선택하거나 올려놓아 주세요.",
                          fileSubject: "기기 이미지 첨부",
                          allowedExtensions: [".jpg", ".jpeg", ".png", ".webp"],
                          click: (files: File[]) => {
                            const file = files[0];
                            if (!file) return FailSnackbar("업로드할 이미지를 선택해 주세요.");
                            uploadColorImageMutation.mutate({ id: parsedId, colorName: color, file });
                          },
                        })
                      }>
                        {imageUrl
                          ? <img src="/static/icons/icon_pencil_blue.svg" alt="" aria-hidden="true" className="size-5" />
                          : <FileUploadSVG className="size-5" aria-hidden="true" />}
                      </OptionIconButton>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[14px] bg-[#F6F7F9] p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-[18px] font-semibold">용량 {result.storages?.length || 0}</h3>
              <Button
                variant="outline"
                size="sm"
                label="+ 용량 추가"
                disabled={busy}
                onClick={() => openModal(MODAL_KEY.DeviceStorage, { deviceId: parsedId })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[...(result.storages ?? [])]
                .sort((left, right) => left.storage.localeCompare(right.storage, "ko", { numeric: true }))
                .map((item) => (
                  <div
                    className="flex self-start flex-col overflow-hidden rounded-[14px] bg-white shadow-[0_3px_14px_rgba(0,0,0,0.09)] transition-shadow hover:shadow-[0_5px_20px_rgba(0,0,0,0.13)]"
                    key={item.storage}
                  >
                    <div className="flex min-w-0 items-center gap-2 bg-black/[0.035] px-4 py-3">
                      <Switch
                        size="small"
                        checked={item.active !== false}
                        disabled={busy}
                        inputProps={{ "aria-label": `${item.storage} 출고가 옵션 활성화` }}
                        onChange={() => storageActiveMutation.mutate({
                          storage: item.storage,
                          active: item.active === false,
                        })}
                      />
                      <span className="min-w-0 flex-1 truncate font-semibold">{item.storage}</span>
                      <OptionIconButton
                        disabled={busy || (result.storages?.length ?? 0) <= 1}
                        danger
                        label={`${item.storage} 삭제`}
                        onClick={() => openModal(MODAL_KEY.Confirm, {
                          title: "용량 삭제",
                          content: `${item.storage} 용량을 삭제하시겠습니까?`,
                          rightButton: {
                            title: "삭제",
                            variant: "outline-red",
                            onClick: () => deleteStorageMutation.mutate(item.storage),
                          },
                        })}
                      >
                        <img src="/static/icons/icon_delete.svg" alt="" aria-hidden="true" className="size-4" />
                      </OptionIconButton>
                    </div>
                    <div className="p-4">
                      <div className="flex w-full min-w-0 items-center gap-2 rounded-[10px] bg-black/[0.025] px-3 py-2">
                        <span className="w-[52px] shrink-0 text-[12px] font-medium text-black/45">출고가</span>
                        <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-black/75">
                          {item.price === null ? "-" : `${item.price.toLocaleString("ko-KR")}원`}
                        </span>
                        <OptionIconButton
                          disabled={busy}
                          label={`${item.storage} 출고가 수정`}
                          onClick={() => openModal(MODAL_KEY.DeviceStorage, {
                            deviceId: parsedId,
                            storage: item,
                          })}
                        >
                          <img src="/static/icons/icon_pencil_blue.svg" alt="" aria-hidden="true" className="size-5" />
                        </OptionIconButton>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
